/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package services

import forecasting.UnifiedForecastingBridge
import ml.categorizeTransactionWithAI
import kotlinx.datetime.*

/**
 * Service for generating category-based expense forecasts
 * Used by both Recommendations and Trends features
 *
 * This service:
 * - Groups transactions by category (using AI categorization)
 * - Calculates monthly spending per category
 * - Generates forecasts using the selected forecasting method
 * - Returns both current spending and forecasts for each category
 */
class CategoryForecastingService(
    private val forecastingBridge: UnifiedForecastingBridge = UnifiedForecastingBridge()
) {

    /**
     * Generate category spending forecasts from transaction data
     * Accepts any transaction type with name, amount, and date fields
     *
     * @param transactions List of transactions (typically last 90 days)
     * @param forecastPeriods Number of future periods to forecast (default: 3)
     * @param methodId Forecasting method ID (e.g., "sma_3", "ses_06", etc.)
     * @param extractName Function to extract merchant name from transaction
     * @param extractAmount Function to extract amount from transaction
     * @param extractDate Function to extract date string (YYYY-MM-DD) from transaction
     * @return CategoryForecastResult with current spending and forecasts per category
     */
    suspend fun <T> generateCategoryForecasts(
        transactions: List<T>,
        forecastPeriods: Int = 3,
        methodId: String = "sma_3", // Default to 3-month simple moving average
        extractName: (T) -> String,
        extractAmount: (T) -> Double,
        extractDate: ((T) -> String)? = null
    ): CategoryForecastResult {

        // Filter for expenses only (Plaid: positive amount = expense)
        val expenses = transactions.filter { extractAmount(it) > 0 }

        if (expenses.isEmpty()) {
            return CategoryForecastResult(
                currentSpending = emptyMap(),
                forecasts = emptyMap(),
                methodUsed = methodId
            )
        }

        // Group transactions by AI-categorized category and date
        data class CategorizedTransaction(val category: String, val amount: Double, val month: String)

        val categorizedTransactions = expenses.map { txn ->
            val name = extractName(txn)
            val amount = extractAmount(txn)
            val date = extractDate?.invoke(txn) ?: "2025-01-01"
            val month = date.substring(0, 7) // Extract YYYY-MM

            val aiResult = categorizeTransactionWithAI(
                merchantName = name,
                amount = amount,
                description = ""
            )
            CategorizedTransaction(aiResult.categoryLevel1, amount, month)
        }

        // Determine all unique months present in the data
        val allMonths = categorizedTransactions.map { it.month }.distinct().sorted()
        println("📆 All months in data: $allMonths")

        // Group by category first
        val transactionsByCategory = categorizedTransactions.groupBy { it.category }

        // Calculate monthly spending per category
        val currentSpending = mutableMapOf<String, Double>()
        val forecasts = mutableMapOf<String, List<Double>>()

        for ((category, transactions) in transactionsByCategory) {
            // Group by month and calculate monthly totals
            val monthlySpending = transactions
                .groupBy { it.month }
                .mapValues { (_, txns) -> txns.sumOf { it.amount } }

            // Fill in $0 for months where this category had no transactions
            val completeMonthlySpending = allMonths.associateWith { month ->
                monthlySpending[month] ?: 0.0
            }

            // Sort by month chronologically
            val sortedMonthlySpending = completeMonthlySpending.toList().sortedBy { it.first }

            println("📅 Category '$category' monthly breakdown: ${sortedMonthlySpending.joinToString { "${it.first}=$${it.second.toInt()}" }}")

            // Use the last (most recent) month as "current" spending
            val currentMonthSpending = sortedMonthlySpending.lastOrNull()?.second ?: 0.0
            currentSpending[category] = currentMonthSpending

            // Create historical data from monthly totals (oldest to newest)
            val historicalData = sortedMonthlySpending.map { it.second }

            // Use the last 3 months (or all if fewer than 3)
            val paddedHistoricalData = if (historicalData.count() < 3) {
                historicalData
            } else {
                historicalData.takeLast(3)
            }

            println("📊 Category '$category' historical data for forecasting: ${paddedHistoricalData.map { "$${it.toInt()}" }}")

            // Generate forecast using the forecasting bridge
            val forecastResult = forecastingBridge.forecastWithMethod(
                methodId = methodId,
                data = paddedHistoricalData,
                periods = forecastPeriods
            )

            if (forecastResult.error == null && forecastResult.values.isNotEmpty()) {
                forecasts[category] = forecastResult.values
                println("🔮 Category '$category' forecast: ${forecastResult.values.map { "$${it.toInt()}" }}, avg=$${forecastResult.values.average().toInt()}")
            } else {
                // Fallback: use current month's spending for forecast
                forecasts[category] = List(forecastPeriods) { currentMonthSpending }
                println("⚠️ Category '$category' forecast failed, using fallback: $${currentMonthSpending.toInt()}")
            }
        }

        return CategoryForecastResult(
            currentSpending = currentSpending,
            forecasts = forecasts,
            methodUsed = methodId
        )
    }

    /**
     * Generate forecasts from pre-calculated category spending
     * Useful when you already have monthly averages
     */
    fun generateForecastsFromSpending(
        categorySpending: Map<String, Double>,
        forecastPeriods: Int = 3,
        methodId: String = "sma_3"
    ): Map<String, List<Double>> {

        val forecasts = mutableMapOf<String, List<Double>>()

        for ((category, monthlyAverage) in categorySpending) {
            // Create historical data points (using the average for all 3 months)
            val historicalData = listOf(monthlyAverage, monthlyAverage, monthlyAverage)

            // Generate forecast using the forecasting bridge
            val forecastResult = forecastingBridge.forecastWithMethod(
                methodId = methodId,
                data = historicalData,
                periods = forecastPeriods
            )

            if (forecastResult.error == null && forecastResult.values.isNotEmpty()) {
                forecasts[category] = forecastResult.values
            } else {
                // Fallback: use current average for forecast
                forecasts[category] = List(forecastPeriods) { monthlyAverage }
            }
        }

        return forecasts
    }

    /**
     * Generate complete financial forecast including income and expenses
     *
     * @param transactions List of transactions (typically last 90 days)
     * @param forecastPeriods Number of future periods to forecast (default: 3)
     * @param methodId Forecasting method ID
     * @param extractName Function to extract merchant name from transaction
     * @param extractAmount Function to extract amount from transaction
     * @param extractDate Function to extract date string (YYYY-MM-DD) from transaction
     * @return CompleteForecastResult with income and expense forecasts
     */
    suspend fun <T> generateCompleteForecast(
        transactions: List<T>,
        forecastPeriods: Int = 3,
        methodId: String = "sma_3",
        extractName: (T) -> String,
        extractAmount: (T) -> Double,
        extractDate: ((T) -> String)? = null
    ): CompleteForecastResult {

        // Separate income and expenses (Plaid: negative = income, positive = expense)
        val income = transactions.filter { extractAmount(it) < 0 }
        val expenses = transactions.filter { extractAmount(it) > 0 }

        // Calculate income forecast (using monthly grouping if dates available)
        val currentMonthlyIncome: Double
        val forecastedMonthlyIncome: Double

        if (extractDate != null) {
            // Group income by month
            val monthlyIncome = income
                .groupBy { extractDate(it).substring(0, 7) }
                .mapValues { (_, txns) -> txns.sumOf { kotlin.math.abs(extractAmount(it)) } }

            // Sort by month chronologically
            val sortedMonthlyIncome = monthlyIncome.toList().sortedBy { it.first }

            println("💰 Income by month (all): ${sortedMonthlyIncome.joinToString { "${it.first}=$${it.second.toInt()}" }}")

            // Get current month in YYYY-MM format
            val currentMonth = Clock.System.now()
                .toLocalDateTime(TimeZone.currentSystemDefault())
                .toString().substring(0, 7)

            // Exclude current incomplete month if it exists in the data
            val completeMonthsIncome = sortedMonthlyIncome.filter { it.first < currentMonth }

            println("💰 Income by month (complete only, excluding current month $currentMonth): ${completeMonthsIncome.joinToString { "${it.first}=$${it.second.toInt()}" }}")

            currentMonthlyIncome = completeMonthsIncome.lastOrNull()?.second ?: 0.0

            val historicalIncomeData = completeMonthsIncome.map { it.second }
            val paddedIncomeData = if (historicalIncomeData.count() < 3) {
                val avg = if (historicalIncomeData.isNotEmpty()) historicalIncomeData.average() else 0.0
                List(3 - historicalIncomeData.count()) { avg } + historicalIncomeData
            } else {
                historicalIncomeData.takeLast(3)
            }

            println("💰 Income data used for SMA-3 forecast: ${paddedIncomeData.joinToString { "$${it.toInt()}" }}")

            val incomeForecastResult = forecastingBridge.forecastWithMethod(
                methodId = methodId,
                data = paddedIncomeData,
                periods = forecastPeriods
            )

            println("💰 Income forecast result: ${incomeForecastResult.values.joinToString { "$${it.toInt()}" }}")

            forecastedMonthlyIncome = if (incomeForecastResult.error == null && incomeForecastResult.values.isNotEmpty()) {
                incomeForecastResult.values.average()
            } else {
                currentMonthlyIncome
            }

            println("💰 Forecasted monthly income (average): $${forecastedMonthlyIncome.toInt()}")
        } else {
            // Fallback: use simple 3-month average
            val totalIncome = income.sumOf { kotlin.math.abs(extractAmount(it)) }
            currentMonthlyIncome = totalIncome / 3.0

            val historicalIncome = listOf(currentMonthlyIncome, currentMonthlyIncome, currentMonthlyIncome)
            val incomeForecastResult = forecastingBridge.forecastWithMethod(
                methodId = methodId,
                data = historicalIncome,
                periods = forecastPeriods
            )

            forecastedMonthlyIncome = if (incomeForecastResult.error == null && incomeForecastResult.values.isNotEmpty()) {
                incomeForecastResult.values.average()
            } else {
                currentMonthlyIncome
            }
        }

        // Generate expense forecasts by category
        val categoryForecast = generateCategoryForecasts(
            transactions = transactions,
            forecastPeriods = forecastPeriods,
            methodId = methodId,
            extractName = extractName,
            extractAmount = extractAmount,
            extractDate = extractDate
        )

        return CompleteForecastResult(
            currentMonthlyIncome = currentMonthlyIncome,
            forecastedMonthlyIncome = forecastedMonthlyIncome,
            categorySpending = categoryForecast.currentSpending,
            categoryForecasts = categoryForecast.forecasts,
            methodUsed = methodId
        )
    }
}

/**
 * Result containing both current spending and forecasts per category
 */
data class CategoryForecastResult(
    val currentSpending: Map<String, Double>,
    val forecasts: Map<String, List<Double>>,
    val methodUsed: String
)

/**
 * Complete forecast result including income and expenses
 */
data class CompleteForecastResult(
    val currentMonthlyIncome: Double,
    val forecastedMonthlyIncome: Double,
    val categorySpending: Map<String, Double>,
    val categoryForecasts: Map<String, List<Double>>,
    val methodUsed: String
)
